import type { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { productUnits } from '@eneco/enumerations/productUnits';
import { ConsumptionDetails } from '@eneco/flows/components';
import { useFormatter } from '@eneco/i18n';
import { Stack, Text, TextLink } from '@eneco/sparky';
import { SensitiveContentComponent } from '@eneco/tracking';
import { DC_Repositories_Base_Enumerations_Label } from '@monorepo-types/dc';

import { DetailsType } from '../../types/enums';

interface Props {
  theme?: DC_Repositories_Base_Enumerations_Label;
  textualData: {
    redeliveryLabel?: string;
    electricityLabel?: string;
    electricityHighLabel?: string;
    electricityLowLabel?: string;
    gasLabel?: string;
    warmthLabel?: string;
    waterLabel?: string;
    description?: string;
  };
  data: {
    hasDoubleMeter?: boolean;
    hasSolarPanels?: boolean;

    usageElectricityHigh?: number;
    usageElectricityLow?: number;
    usageGas?: number;
    usageWarmth?: number;
    usageWater?: number;

    solarPanelsOutput?: number;
  };
  editButton?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
}

export const OverviewConsumption: FC<Props> = ({
  theme,
  textualData: {
    description,
    redeliveryLabel,
    electricityLabel,
    electricityHighLabel,
    electricityLowLabel,
    gasLabel,
    warmthLabel,
    waterLabel,
  },
  data: {
    hasDoubleMeter,
    hasSolarPanels,

    usageElectricityHigh,
    usageElectricityLow,
    usageGas,
    usageWarmth,
    usageWater,

    solarPanelsOutput,
  },
  editButton,
}) => {
  const { number } = useFormatter();
  const labelPrefix = (label?: string) => (label ? `${label}: ` : '');

  return (
    <Stack direction="column" alignX={{ initial: 'start', md: 'center' }} gap="4">
      <Text size="BodyXS">
        <SensitiveContentComponent contentMask="masked address">
          <RichText html={description} />
        </SensitiveContentComponent>
      </Text>

      <Stack direction={{ initial: 'column', md: 'row' }} alignX={{ initial: 'start', md: 'center' }} gap="2" wrap>
        {usageElectricityHigh && (
          <ConsumptionDetails theme={theme} type={DetailsType.Electricity} title={electricityLabel}>
            {hasDoubleMeter
              ? `${labelPrefix(electricityHighLabel)}${number.noFractionDigits(
                  usageElectricityHigh,
                )} ${productUnits.electricity} ${labelPrefix(
                  electricityLowLabel,
                )}${number.noFractionDigits(usageElectricityLow ?? 0)} ${productUnits.electricity}`
              : `${number.noFractionDigits(usageElectricityHigh ?? 0)} ${productUnits.electricity}`}
          </ConsumptionDetails>
        )}

        {usageGas && (
          <ConsumptionDetails theme={theme} type={DetailsType.Gas} title={gasLabel}>
            {number.noFractionDigits(usageGas)} {productUnits.gas}
          </ConsumptionDetails>
        )}

        {usageWarmth && (
          <ConsumptionDetails theme={theme} type={DetailsType.Warmth} title={warmthLabel}>
            {number.noFractionDigits(usageWarmth)} {productUnits.warmth}
          </ConsumptionDetails>
        )}

        {usageWater && (
          <ConsumptionDetails theme={theme} type={DetailsType.Water} title={waterLabel}>
            {number.noFractionDigits(usageWater)} {productUnits.tapWater}
          </ConsumptionDetails>
        )}

        {hasSolarPanels && (
          <ConsumptionDetails theme={theme} type={DetailsType.Redelivery} title={redeliveryLabel}>
            {number.noFractionDigits(solarPanelsOutput ?? 0)} {productUnits.electricity}
          </ConsumptionDetails>
        )}
      </Stack>

      {editButton && (
        <Text size="BodyXS">
          <TextLink href={editButton.href} onClick={editButton.onClick}>
            {editButton.label}
          </TextLink>
        </Text>
      )}
    </Stack>
  );
};
